import React, { createContext, useState, useEffect, useMemo, useContext } from 'react';

import { CPLAT_API_ROUTES, usePost } from '@/api';
import { useAppSelector } from '@/redux/store';
import { getActivePeriod } from '../V3/Statistics/_utils';
import { formatDateToKST } from '@/utils/parser';
import {
  CalendarDaily,
  MonthlySales,
  SalesInfo,
  SettleInfo,
  SalesMarket,
  SalesProduct,
  SalesStore,
  SalesInfoInitial,
  SettleInfoInitial,
  MonthlySalesInitial,
  Market,
} from '@/types/statistics';
import { CplatApiResponse, UserData } from '@/types/api';
interface StatisticsContextType {
  salesInfo: SalesInfo;
  settleInfo: SettleInfo;
  calendarDailyList: Array<CalendarDaily>;
  monthlySalesList: Array<MonthlySales>;
  salesMarketList: Array<SalesMarket>;
  salesProductList: Array<SalesProduct>;
  salesStoreList: Array<SalesStore>;
  totalSalesAmount: number;
  activedMonthlySales: MonthlySales;
  setActivedMonthlySales: React.Dispatch<React.SetStateAction<MonthlySales>>;
  isLoading: boolean;
  selectedDate: string;
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>;
  dailyMarketList: Market[];
  dailySalesInfo: SalesInfo;
  dailySettleInfo: SettleInfo;
  isDailyLoading: boolean;
  isRegisteredMarket: boolean;
}

export const StatisticsContext = createContext<StatisticsContextType | undefined>(undefined);

interface StatisticsProviderProps {
  children: React.ReactNode;
}

export const StatisticsProvider = ({ children }: StatisticsProviderProps) => {
  // 상태 초기값 지정
  const user = useAppSelector((state) => state.user);
  const [salesInfo, setSalesInfo] = useState<SalesInfo>(SalesInfoInitial);
  const [settleInfo, setSettleInfo] = useState<SettleInfo>(SettleInfoInitial);
  const [calendarDailyList, setCalendarDailyList] = useState<Array<CalendarDaily>>([]);
  const [monthlySalesList, setMonthlySalesList] = useState<Array<MonthlySales>>([]);
  const [salesMarketList, setSalesMarketList] = useState<Array<SalesMarket>>([]);
  const [salesProductList, setSalesProductList] = useState<Array<SalesProduct>>([]);
  const [salesStoreList, setSalesStoreList] = useState<Array<SalesStore>>([]);
  const [totalSalesAmount, setTotalSalesAmount] = useState<number>(0);
  const [activedMonthlySales, setActivedMonthlySales] = useState<MonthlySales>(MonthlySalesInitial);
  const [isLoading, setIsLoading] = useState(false);

  const [dailyMarketList, setDailyMarketList] = useState<Market[]>([]);
  const [dailySalesInfo, setDailySalesInfo] = useState<SalesInfo>(SalesInfoInitial);
  const [dailySettleInfo, setDailySettleInfo] = useState<SettleInfo>(SettleInfoInitial);
  const [isDailyLoading, setIsDailyLoading] = useState<boolean>(false);
  // 오늘 날짜를 string으로 변환
  const todayStr = formatDateToKST(new Date()); // 예: '2025-06-15'

  // 선택된 오늘의 날짜
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);

  // API hooks
  const { mutate: getCalendar } = usePost(CPLAT_API_ROUTES.getCalendar);
  const { mutate: getPeriodReport } = usePost(CPLAT_API_ROUTES.getPeriodReport);
  const { mutate: getPeriodDetail } = usePost(CPLAT_API_ROUTES.getPeriodDetail);

  // 최초 로딩 시 월별 리포트 및 캘린더 데이터 불러오기
  useEffect(() => {
    if (!user?.CplatToken) return;
    setIsLoading(true);

    // 1. 월별 리포트
    const { dateFrom, dateTo } = getActivePeriod(todayStr, 7, true);
    getPeriodReport(
      {
        cplatToken: user.CplatToken,
        dateFrom,
        dateTo,
      },
      {
        onSuccess(res) {
          const { data, code } = res.data;
          if (code === '200' && data.length > 0) {
            const { TotalSalesAmount, MonthlySalesList } = data[0].SalesInfo;
            setTotalSalesAmount(TotalSalesAmount);
            setMonthlySalesList(MonthlySalesList);
            setActivedMonthlySales(
              MonthlySalesList[MonthlySalesList.length - 1] ?? MonthlySalesInitial,
            );
          }
        },

        onSettled() {
          setIsLoading(false);
        },
      },
    );

    // 2. 캘린더 데이터
    const { dateFrom: calFrom, dateTo: calTo } = getActivePeriod(todayStr, 1, false);
    getCalendar(
      {
        cplatToken: user.CplatToken,
        dateFrom: calFrom,
        dateTo: calTo,
      },
      {
        onSuccess(res) {
          const data = res.data.data?.[0];
          if (data) {
            setSalesInfo(data.SalesInfo);
            setSettleInfo(data.SettleInfo);
            setCalendarDailyList(data.CalendarDailyList);
          }
        },
        onSettled() {
          setIsLoading(false);
        },
      },
    );
  }, [user?.CplatToken]);

  useEffect(() => {
    if (!user?.CplatToken || !activedMonthlySales?.Month) return;
    setIsLoading(true);

    // 1) 월별 상세 데이터 호출 (기존)
    const [year, month] = activedMonthlySales.Month.split('-').map(Number);
    const activedDate = new Date(year, month - 1, 1);
    const activedDateStr = formatDateToKST(activedDate);

    const isCurrentMonth = new Date().getFullYear() === year && new Date().getMonth() + 1 === month;
    const { dateFrom, dateTo } = getActivePeriod(activedDateStr, 1, isCurrentMonth);

    getPeriodDetail(
      {
        cplatToken: user.CplatToken,
        dateFrom,
        dateTo,
      },
      {
        onSuccess(res) {
          const { data, code } = res.data;
          if (code === '200' && data.length > 0) {
            setSalesMarketList(data[0].SalesMarketList ?? []);
            setSalesProductList(data[0].SalesProductList ?? []);
            setSalesStoreList(data[0].SalesStoreList ?? []);
          } else {
            setSalesMarketList([]);
            setSalesProductList([]);
            setSalesStoreList([]);
          }
        },
        onSettled() {
          setIsLoading(false);
        },
      },
    );

    // 2) 매출/정산 데이터 갱신 API 호출 추가
    // getCalendar API 호출 (기간은 activedMonthlySales 월의 1일 ~ 말일)
    const dateFromCalendar = new Date(year, month - 1, 1);
    const dateToCalendar = new Date(year, month, 0); // 해당 월 마지막 날

    getCalendar(
      {
        cplatToken: user.CplatToken,
        dateFrom: formatDateToKST(dateFromCalendar),
        dateTo: formatDateToKST(dateToCalendar),
      },
      {
        onSuccess(res) {
          const data = res.data.data?.[0];
          if (data) {
            setSalesInfo(data.SalesInfo);
            setSettleInfo(data.SettleInfo);
            setCalendarDailyList(data.CalendarDailyList);
          }
        },
        onSettled() {
          setIsLoading(false);
        },
      },
    );
  }, [user?.CplatToken, activedMonthlySales]);

  const { mutate: getCalendarDaily } = usePost(CPLAT_API_ROUTES.getCalendarDaily);

  // 날짜가 바뀔 때마다 상세 데이터 fetch
  useEffect(() => {
    if (!selectedDate) return;
    setIsDailyLoading(true);
    getCalendarDaily(
      { cplatToken: user.CplatToken, dateFrom: selectedDate, dateTo: selectedDate },
      {
        onSuccess(res) {
          const { data, code } = res?.data;
          if (code === '200' && data.length > 0) {
            setDailyMarketList(data[0].MarketList ?? []);
            setDailySalesInfo(data[0].SalesInfo ?? SalesInfoInitial);
            setDailySettleInfo(data[0].SettleInfo ?? SettleInfoInitial);
          } else {
            setDailyMarketList([]);
            setDailySalesInfo(SalesInfoInitial);
            setDailySettleInfo(SettleInfoInitial);
          }
        },
        onSettled() {
          setIsDailyLoading(false);
        },
      },
    );
  }, [selectedDate]);

  const { mutate: getRegisteredMarketInfo } = usePost(CPLAT_API_ROUTES.getRegisteredMarketInfo);
  const [isRegisteredMarket, setIsRegisteredMarket] = useState<boolean>(true);

  const handleGetRegisteredMarketInfo = () => {
    getRegisteredMarketInfo(
      {
        cplatToken: user.CplatToken,
      },
      {
        onSuccess(res) {
          const { code } = res.data as CplatApiResponse<UserData[]>;
          if (code === '200') {
            setIsRegisteredMarket(true);
          } else {
            setIsRegisteredMarket(false);
          }
        },
      },
    );
  };

  useEffect(() => {
    handleGetRegisteredMarketInfo();
  }, []);

  const value = useMemo(
    () => ({
      salesInfo,
      settleInfo,
      calendarDailyList,
      monthlySalesList,
      salesMarketList,
      salesProductList,
      salesStoreList,
      totalSalesAmount,
      activedMonthlySales,
      setActivedMonthlySales,
      isLoading,
      selectedDate,
      setSelectedDate,
      dailyMarketList,
      dailySalesInfo,
      dailySettleInfo,
      isDailyLoading,
      isRegisteredMarket,
    }),
    [
      salesInfo,
      settleInfo,
      calendarDailyList,
      monthlySalesList,
      salesMarketList,
      salesProductList,
      salesStoreList,
      totalSalesAmount,
      activedMonthlySales,
      isLoading,
      selectedDate,
      dailyMarketList,
      dailySalesInfo,
      dailySettleInfo,
      isDailyLoading,
      isRegisteredMarket,
    ],
  );

  return <StatisticsContext.Provider value={value}>{children}</StatisticsContext.Provider>;
};

// 커스텀 훅 제공 (추천)
export const useStatistics = () => {
  const context = useContext(StatisticsContext);
  if (!context) throw new Error('useStatistics must be used within a StatisticsProvider');
  return context;
};
